import React from 'react';
import { Button, Flex, Typography, theme } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { isAffectedByPricing2026, PRICING_UPDATE_2026_ARTICLE_URL } from '@prerender/billing-shared';

import { useSelector, useCharbeePlanId, usePrerenderUserId } from '../../../hooks/usePrerenderUser';
import { neverExpires, useDismissibleBanner } from '../../../hooks/useDismissibleBanner';

const PAYING_BODY_COPY =
  'Pricing for Starter, Growth, and Pro is updating from your next billing cycle on or after Sept 15. AI Insights is already included at no extra cost.';
const TRIAL_BODY_COPY =
  'Pricing for Starter, Growth, and Pro is updating. Your free trial is unaffected for the rest of the trial, and your first invoice on or after 15 September 2026 will be at the new price.';
const CTA_LABEL = "See what's changing";
const DISMISS_LABEL = 'Dismiss';
export const DISMISSAL_KEY_PREFIX = 'pricing-update-2026-banner-dismissed';

// The announcement runs to end of day 15 October 2026 (UTC): one monthly cycle past the
// 15 September change, by which point every monthly subscriber has renewed onto the new rate
// and the banner's future tense would be false.
const BANNER_WINDOW_END = Date.UTC(2026, 9, 16);

type RepricedSubscription = Parameters<typeof isAffectedByPricing2026>[0];

export function isPriceUpdate2026BannerVisible(subscription: RepricedSubscription, now: number = Date.now()): boolean {
  return now < BANNER_WINDOW_END && isAffectedByPricing2026(subscription, new Date(now));
}

function PriceUpdate2026Banner() {
  const { token } = theme.useToken();
  const chargebeePlanId = useCharbeePlanId();
  const customPrice = useSelector((state) => state.prerenderUser.customPrice);
  const cancelledAt = useSelector((state) => state.prerenderUser.cancelledAt);
  const isTrial = useSelector((state) => state.prerenderUser.isTrial);
  const prerenderUserId = usePrerenderUserId();
  const { isDismissed, dismiss } = useDismissibleBanner(DISMISSAL_KEY_PREFIX, prerenderUserId, neverExpires);

  if (isDismissed || !isPriceUpdate2026BannerVisible({ chargebeePlanId, customPrice, cancelledAt })) {
    return null;
  }

  return (
    <Flex
      align="center"
      justify="space-between"
      gap={token.marginSM}
      style={{
        background: token.colorFillTertiary,
        border: `${token.lineWidth}px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding: token.paddingSM,
        marginBottom: token.margin,
      }}
    >
      <Typography.Text>{isTrial ? TRIAL_BODY_COPY : PAYING_BODY_COPY}</Typography.Text>
      <Typography.Link href={PRICING_UPDATE_2026_ARTICLE_URL} target="_blank" rel="noreferrer">
        <Button type="primary">{CTA_LABEL}</Button>
      </Typography.Link>
      <Button type="link" icon={<CloseOutlined />} style={{ color: token.colorPrimary }} onClick={dismiss}>
        {DISMISS_LABEL}
      </Button>
    </Flex>
  );
}

export default PriceUpdate2026Banner;
